import {
  GroupableRisk,
  RiskGroup,
} from '@/sections/vulnerabilities/types/grouping.types';
import { Risk, RiskSeverity } from '@/types';

/**
 * Severity sort order mapping for client-side sorting
 * Maps both single-letter codes (C, H, M) and lowercase words (critical, high, medium)
 */
const SEVERITY_ORDER: Record<string, number> = {
  // Single-letter codes (from RiskSeverity enum values)
  C: 0,
  H: 10,
  M: 20,
  L: 30,
  I: 40,
  E: 50,
  // Lowercase word forms (from backend groupBy response)
  critical: 0,
  high: 10,
  medium: 20,
  low: 30,
  info: 40,
  exposure: 50,
};

/**
 * Get a comparable value from a group for the given sort field
 */
function getGroupSortValue(group: RiskGroup, orderBy: string): string | number {
  switch (orderBy) {
    case 'name':
      return group.fields.name.toLowerCase();
    case 'priority':
    case 'severity': {
      // Severity is encoded as the second character of status (e.g., "OH" → "H")
      const sevChar = group.fields.status?.[1] || '';
      return SEVERITY_ORDER[sevChar as RiskSeverity] ?? 99;
    }
    case 'status':
      return group.fields.status.toLowerCase();
    case 'created':
      return group.min_created || '';
    case 'visited':
      return group.max_visited || '';
    default:
      return group.fields.name.toLowerCase();
  }
}

/**
 * Get a comparable value from a child risk for the given sort field
 */
function getChildSortValue(child: Risk, orderBy: string): string | number {
  switch (orderBy) {
    case 'name':
      return (child.name || '').toLowerCase();
    case 'priority':
    case 'severity':
      return SEVERITY_ORDER[child.status?.[1] as RiskSeverity] ?? 99;
    case 'status':
      return (child.status?.[0] || '').toLowerCase();
    case 'created':
      return child.created || '';
    case 'visited':
      return child.visited || '';
    default:
      return (child.name || '').toLowerCase();
  }
}

/**
 * Compare two values for sorting
 */
function compareValues(
  a: string | number,
  b: string | number,
  descending: boolean
): number {
  let result: number;
  if (typeof a === 'number' && typeof b === 'number') {
    result = a - b;
  } else {
    result = String(a).localeCompare(String(b));
  }
  return descending ? -result : result;
}

/**
 * Flattens grouped risks into a single array suitable for table rendering
 *
 * @param groups - Array of risk groups from backend
 * @param expandedGroups - Set of group keys that are currently expanded
 * @param childrenByKey - Map of group keys to their child risks
 * @param sort - Optional sort configuration for client-side sorting
 * @returns Flat array with group headers and children
 */
export function flattenGroupedData(
  groups: RiskGroup[],
  expandedGroups: Set<string>,
  childrenByKey: Map<string, Risk[]>,
  sort?: { orderBy: string; descending: boolean }
): GroupableRisk[] {
  const result: GroupableRisk[] = [];

  // Sort groups if sort config provided
  let sortedGroups = groups;
  if (sort) {
    sortedGroups = [...groups].sort((a, b) =>
      compareValues(
        getGroupSortValue(a, sort.orderBy),
        getGroupSortValue(b, sort.orderBy),
        sort.descending
      )
    );
  }

  sortedGroups.forEach(group => {
    // Generate group key (status encodes both lifecycle status and severity, e.g., "OH" = Open+High)
    const groupKey = `${group.fields.name}::${group.fields.status}`;
    const isExpanded = expandedGroups.has(groupKey);

    // Derive severity from status[1] (the second character encodes severity)
    const severityChar = group.fields.status?.[1] || '';

    // Create synthetic Risk object for group header
    const groupHeader: GroupableRisk = {
      // Required Risk fields (synthetic values for group header)
      key: `group::${groupKey}`,
      name: group.fields.name,
      title: group.fields.max_title || group.fields.name,
      status: group.fields.status,
      created: group.min_created || '',
      visited: group.max_visited || '',
      comment: '',
      tags: [],
      class: 'risk',
      username: '',
      dns: '',
      updated: group.max_visited || '',
      ttl: 0,
      source: '',
      origins: [],
      history: [],
      priority: 0,

      // Group header metadata
      _isGroupHeader: true,
      _groupKey: groupKey,
      _childCount: group.count,
      _isExpanded: isExpanded,
      _minCreated: group.min_created,
      _maxVisited: group.max_visited,
      _severity: severityChar,
    };

    result.push(groupHeader);

    // Add children if group is expanded
    if (isExpanded) {
      let children = childrenByKey.get(groupKey) || [];

      // Sort children if sort config provided
      if (sort && children.length > 1) {
        children = [...children].sort((a, b) =>
          compareValues(
            getChildSortValue(a, sort.orderBy),
            getChildSortValue(b, sort.orderBy),
            sort.descending
          )
        );
      }

      children.forEach(child => {
        const childRow: GroupableRisk = {
          ...child,
          _isChild: true,
          _groupKey: groupKey,
        };
        result.push(childRow);
      });
    }
  });

  return result;
}
